/**
 * lib/hooks/use-pii-mask.ts
 *
 * Formatters for crew PII on screen, driven by the admin's Demo privacy mode
 * switch (Settings). When the mode is off each formatter returns the value
 * unchanged. Staff logins (ops, finance, vendors) stay visible; only users with
 * the CREW role are masked by userName().
 */
import { useMemo } from 'react';
import { useAppStore } from '@/lib/store';
import { maskMiddle, maskText, swapAirlineCode } from '@/lib/compliance/demo-mask';
import { DEMO_BRAND_IDENTITY } from '@/lib/branding';
import type { NotificationOutboxEntry, User } from '@/lib/types';

export interface PiiMask {
  enabled: boolean;
  name: (value: string | null | undefined) => string;
  email: (value: string | null | undefined) => string;
  phone: (value: string | null | undefined) => string;
  userName: (user: Pick<User, 'full_name' | 'role'>) => string;
  /** A flight number or duty label with the tenant's airline code shown as the demo code. */
  flight: (value: string | null | undefined) => string;
}

export function usePiiMask(): PiiMask {
  const enabled = useAppStore((s) => s.piiMaskEnabled);
  const airlineCode = useAppStore((s) => s.branding?.iata_code ?? null);
  return useMemo(() => {
    const format = (value: string | null | undefined): string =>
      enabled ? maskMiddle(value) : (value ?? '');
    const userName = (user: Pick<User, 'full_name' | 'role'>): string =>
      user.role === 'CREW' ? format(user.full_name) : user.full_name;
    const flight = (value: string | null | undefined): string =>
      enabled ? swapAirlineCode(value ?? '', airlineCode, DEMO_BRAND_IDENTITY.iata_code) : (value ?? '');
    return { enabled, name: format, email: format, phone: format, userName, flight };
  }, [enabled, airlineCode]);
}

/** An outbox entry as shown on screen: a crew recipient address is masked, and
 * emails, phones and the related booking's crew names are masked in the
 * subject and body, and flight numbers carry the demo airline code. Returns the entry unchanged when the mode is off. */
export function useMaskedOutboxEntry(entry: NotificationOutboxEntry): NotificationOutboxEntry {
  const { enabled, flight } = usePiiMask();
  const bookings = useAppStore((s) => s.bookings);
  const crew = useAppStore((s) => s.crew);
  return useMemo(() => {
    if (!enabled) return entry;
    const booking = entry.related_booking_id ? bookings.find((b) => b.id === entry.related_booking_id) : undefined;
    const crewIds = booking ? [booking.primary_crew_id, booking.companion_crew_id] : [];
    const names = crew.filter((c) => crewIds.includes(c.id)).map((c) => c.full_name);
    return {
      ...entry,
      to: entry.recipient_type === 'CREW' ? maskMiddle(entry.to) : entry.to,
      subject: flight(maskText(entry.subject, names)),
      body: flight(maskText(entry.body, names)),
    };
  }, [enabled, flight, entry, bookings, crew]);
}
